import React, { useState, useEffect } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, TrendingUp, Info, PieChart } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

export const SIPCalculatorPage = () => {
  const [monthlyAmount, setMonthlyAmount] = useState(10000);
  const [years, setYears] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [stepUpPercent, setStepUpPercent] = useState(0);
  const [result, setResult] = useState(null);
  const [selectedFund, setSelectedFund] = useState("");
  const [funds, setFunds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const fetchFunds = async () => {
        try {
          const res = await axios.get(`${API}/funds/search?q=${searchQuery}&limit=10`);
          setFunds(res.data.funds || []);
        } catch (e) {
          console.error(e);
        }
      };
      fetchFunds();
    }
  }, [searchQuery]);

  const calculateSIP = () => {
    const P = monthlyAmount;
    const r = expectedReturn / 100 / 12;
    const n = years * 12;
    
    let totalInvested = 0;
    let futureValue = 0;
    
    if (stepUpPercent > 0) {
      // Step-up SIP calculation
      let currentMonthly = P;
      for (let year = 0; year < years; year++) {
        for (let month = 0; month < 12; month++) {
          const monthsRemaining = n - (year * 12 + month);
          futureValue += currentMonthly * Math.pow(1 + r, monthsRemaining);
          totalInvested += currentMonthly;
        }
        currentMonthly *= (1 + stepUpPercent / 100);
      }
    } else {
      // Regular SIP
      totalInvested = P * n;
      futureValue = P * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
    }
    
    const wealth = Math.round(futureValue);
    const gains = wealth - totalInvested;
    
    setResult({
      totalInvested: Math.round(totalInvested),
      estimatedReturns: Math.round(gains),
      totalValue: wealth,
      absoluteReturns: ((gains / totalInvested) * 100).toFixed(1)
    });
  };

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              SIP <span className="gold-text">Calculator</span>
            </h1>
            <p className="text-muted-foreground">
              Plan your systematic investment and see how your wealth can grow over time
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Calculator Inputs */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Investment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Monthly SIP Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={monthlyAmount}
                      onChange={(e) => setMonthlyAmount(Number(e.target.value))}
                      className="pl-8"
                      data-testid="sip-amount-input"
                    />
                  </div>
                  <input
                    type="range"
                    min="500"
                    max="100000"
                    step="500"
                    value={monthlyAmount}
                    onChange={(e) => setMonthlyAmount(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Investment Period (Years)</Label>
                  <Input
                    type="number"
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    min="1"
                    max="40"
                    data-testid="sip-years-input"
                  />
                  <input
                    type="range"
                    min="1"
                    max="40"
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected Annual Return (%)</Label>
                  <Input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(Number(e.target.value))}
                    min="1"
                    max="30"
                    step="0.5"
                    data-testid="sip-return-input"
                  />
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="0.5"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Annual Step-up (% increase per year)</Label>
                  <Input
                    type="number"
                    value={stepUpPercent}
                    onChange={(e) => setStepUpPercent(Number(e.target.value))}
                    min="0"
                    max="25"
                    data-testid="sip-stepup-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Set to 0 for regular SIP, or enter % to increase SIP yearly
                  </p>
                </div>

                <Button onClick={calculateSIP} className="w-full" data-testid="calculate-sip-btn">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Returns
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-6">
              {result && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      Your SIP Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-secondary">
                        <p className="text-sm text-muted-foreground">Total Invested</p>
                        <p className="text-xl font-bold text-foreground">{formatCurrency(result.totalInvested)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-green-500/10">
                        <p className="text-sm text-muted-foreground">Est. Returns</p>
                        <p className="text-xl font-bold text-green-500">{formatCurrency(result.estimatedReturns)}</p>
                      </div>
                    </div>
                    
                    <div className="p-6 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
                      <p className="text-sm text-muted-foreground mb-1">Total Value</p>
                      <p className="text-3xl font-bold gold-text">{formatCurrency(result.totalValue)}</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Absolute Returns: <span className="text-green-500 font-medium">{result.absoluteReturns}%</span>
                      </p>
                    </div>

                    {/* Visual breakdown */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Investment</span>
                        <span>Returns</span>
                      </div>
                      <div className="h-4 rounded-full overflow-hidden bg-secondary flex">
                        <div 
                          className="bg-primary/60 h-full"
                          style={{ width: `${(result.totalInvested / result.totalValue) * 100}%` }}
                        />
                        <div 
                          className="bg-green-500 h-full"
                          style={{ width: `${(result.estimatedReturns / result.totalValue) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{((result.totalInvested / result.totalValue) * 100).toFixed(0)}%</span>
                        <span>{((result.estimatedReturns / result.totalValue) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Info Card */}
              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground space-y-2">
                      <p><strong>What is SIP?</strong> Systematic Investment Plan lets you invest a fixed amount regularly in mutual funds.</p>
                      <p><strong>Step-up SIP:</strong> Increase your SIP amount annually to accelerate wealth creation.</p>
                      <p><strong>Note:</strong> Returns shown are estimates. Actual returns depend on market conditions.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
